import React from "react";
import { useSearchParams } from "react-router-dom";
import { apiBukuKas, apiGetPengaturan, formatApiErrorDetail } from "@/lib/api";
import { rupiah, tanggalIndo } from "@/lib/format";
import * as W from "@/lib/waktu";

// Print-friendly cash book report (A4). Same filters as the Buku Kas page;
// every figure comes from the server.
export default function CetakKas() {
  const [params] = useSearchParams();
  const [d, setD] = React.useState(null);
  const [cfg, setCfg] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const p = Object.fromEntries(params.entries());
    // v2.18 (Finance 2026-10-24): without one account the report is grouped per account with subtotals
    if (!p.akun) p.per_akun = "true";
    Promise.all([apiBukuKas(p), apiGetPengaturan()])
      .then(([a, b]) => { setD(a); setCfg(b); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [params]);

  if (error) return <div data-testid="kas-cetak-error" className="p-6 text-red-600">{error}</div>;
  if (!d) return <div className="p-6 text-slate-500">Memuat…</div>;
  const td = "border border-slate-400 px-2 py-1";
  const kepala = (
    <thead><tr className="bg-slate-100">
      <th className={td}>Tanggal</th><th className={td}>No</th><th className={td}>Kategori</th><th className={td}>Keterangan</th>
      <th className={`${td} text-right`}>Debet</th><th className={`${td} text-right`}>Kredit</th><th className={`${td} text-right`}>Saldo</th></tr></thead>
  );
  const baris = (r) => {
    const e = r.efektif; const batal = e.status === "BATAL";
    return (
      <tr key={r.id} className={batal ? "text-slate-500" : ""}>
        <td className={td}>{tanggalIndo(e.tanggal)}</td><td className={td}>{r.nomor}</td><td className={td}>{e.kategori.nama}</td>
        <td className={td}>{e.keterangan}{batal ? " (DIBATALKAN)" : r.dikoreksi ? " (dikoreksi)" : ""}</td>
        <td className={`${td} text-right`}>{e.debet ? rupiah(e.debet) : ""}</td>
        <td className={`${td} text-right`}>{e.kredit ? rupiah(e.kredit) : ""}</td>
        <td className={`${td} text-right`}>{rupiah(r.saldo)}</td>
      </tr>
    );
  };
  // one account's book: opening balance, entries with the running balance, totals, closing balance
  const buku = (b, id, judulJumlah = "Jumlah", judulAkhir = "Saldo akhir") => (
    <table className="mt-2 w-full border-collapse">
      {kepala}
      <tbody>
        <tr><td className={td}>{tanggalIndo(d.dari)}</td><td className={td} colSpan={5}>Saldo awal</td><td className={`${td} text-right`} data-testid={`${id}-awal`}>{rupiah(b.saldo_awal)}</td></tr>
        {b.entri.map(baris)}
        {b.entri.length === 0 && <tr><td className={`${td} text-center italic text-slate-500`} colSpan={7}>Tidak ada transaksi pada periode ini.</td></tr>}
        <tr className="font-bold"><td className={td} colSpan={4}>{judulJumlah}</td><td className={`${td} text-right`} data-testid={`${id}-debet`}>{rupiah(b.total_debet)}</td>
          <td className={`${td} text-right`} data-testid={`${id}-kredit`}>{rupiah(b.total_kredit)}</td><td className={td} /></tr>
        <tr className="font-bold"><td className={td} colSpan={6}>{judulAkhir}</td><td className={`${td} text-right`} data-testid={`${id}-akhir`}>{rupiah(b.saldo_akhir)}</td></tr>
      </tbody>
    </table>
  );
  const kelompok = d.kelompok || null;
  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-6 print:py-0">
      <style>{"@page { size: A4; margin: 12mm; } @media print { .no-print { display: none !important; } }"}</style>
      <div className="no-print mx-auto mb-4 flex justify-center">
        <button data-testid="kas-cetak-print" onClick={() => window.print()} className="rounded bg-soq-biru px-4 py-2 text-sm text-white">Cetak</button>
      </div>
      <div data-testid="kas-paper" className="mx-auto bg-white text-black p-8 text-[11px] leading-snug" style={{ width: "210mm", maxWidth: "100%" }}>
        <div className="text-center">
          <div className="text-base font-bold">{cfg?.nama_usaha || ""}</div>
          <div className="text-sm font-bold" data-testid="kas-cetak-judul">LAPORAN BUKU KAS — {d.akun_info ? d.akun_info.nama.toUpperCase() : "SEMUA AKUN"}</div>
          <div data-testid="kas-cetak-periode">Periode {tanggalIndo(d.dari)} s.d. {tanggalIndo(d.sampai)}</div>
          {d.difilter && <div className="italic">Daftar difilter; saldo tetap menghitung semua entri.</div>}
        </div>
        {kelompok ? (<>
          {kelompok.length === 0 && <div className="mt-4 text-center italic text-slate-500">Tidak ada saldo atau transaksi pada periode ini.</div>}
          {kelompok.map((g) => (
            <section key={g.akun.kode} data-testid={`kas-cetak-akun-${g.akun.kode}`} className="mt-5 break-inside-avoid-page">
              <div className="text-[12px] font-bold">{g.akun.nama}</div>
              {buku(g, `kas-cetak-${g.akun.kode}`, `Subtotal ${g.akun.nama}`, `Saldo akhir ${g.akun.nama}`)}
            </section>
          ))}
          <table className="mt-5 w-1/2 ml-auto border-collapse font-bold" data-testid="kas-cetak-total">
            <tbody>
              <tr><td className={td}>Saldo awal semua akun</td><td className={`${td} text-right`} data-testid="kas-cetak-awal">{rupiah(d.saldo_awal)}</td></tr>
              <tr><td className={td}>Jumlah debet semua akun</td><td className={`${td} text-right`} data-testid="kas-cetak-debet">{rupiah(d.total_debet)}</td></tr>
              <tr><td className={td}>Jumlah kredit semua akun</td><td className={`${td} text-right`} data-testid="kas-cetak-kredit">{rupiah(d.total_kredit)}</td></tr>
              <tr><td className={td}>Saldo akhir semua akun</td><td className={`${td} text-right`} data-testid="kas-cetak-akhir">{rupiah(d.saldo_akhir)}</td></tr>
            </tbody>
          </table>
          {(d.transfer_masuk > 0 || d.transfer_keluar > 0) && <div className="mt-1 italic">Transfer antar akun tercatat di kedua akun (keluar dari akun asal, masuk ke akun tujuan) dan saling meniadakan di jumlah semua akun.</div>}
        </>) : buku(d, "kas-cetak")}
        {d.per_kategori.length > 0 && (
          <table className="mt-4 w-1/2 border-collapse">
            <thead><tr className="bg-slate-100"><th className={td}>Rekap kategori</th><th className={td}>Jenis</th><th className={`${td} text-right`}>Jumlah</th></tr></thead>
            <tbody>{d.per_kategori.map((k) => (
              <tr key={k.kode}><td className={td}>{k.nama}</td><td className={td}>{k.jenis === "MASUK" ? "Masuk" : "Keluar"}</td><td className={`${td} text-right`}>{rupiah(k.jumlah)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <div className="mt-4 text-right">Dicetak {W.waktu(new Date())}</div>
      </div>
    </div>
  );
}
